<template lang='pug'>
.fp-tree
  .fp-tree-main-header
    h1(v-html='options.name')
    .fp-tree-root-actions
      .fp-tree-root-action(:key='index', v-for='action, index in options.actions')
        fpui-button(
          :color="action.color||'blue'",
          reverse
          icon-left
          @click='call(action)',
          :icon='action.icon'
        ) {{ action.label }}
      .searchInput
        input(:value='search', @input='searchChange', :placeholder="'search.placeholder' | translate")
        i.fpui.fpui-search
  .table.fp-tree-content(cellspacing="0" cellpadding="0")
    .tr.fp-tree-headers
      fp-tree-header(
        :column-ratio='columnRatio',
        :key='index',
        :column-item='column',
        :index='index',
        @click="sort(column,index)",
        v-for='column, index in options.columns'
        :column='column'
        :sorted='sorted'
      )
      .td.fp-tree-cell.fp-tree-header
    .fp-tree-root(ref="treeRoot")
      .tr.fp-tree-item.placeholder(v-if='!rows.length', @click='call(options.placeholder)')
        .td(:colspan='options.columns.length+1') {{ options.placeholder.label}}
      virtual-list( :size="cellSize" :remain="remainSize", ref="virtualList",v-if="rows.length" class="virtual")
        div(v-for="item, index in rows" :key='item.id||index')
          fp-tree-item(
            :column-ratio='columnRatio',
            :options='options',
            :is-last='index === rows.length - 1'
            :item='item'
            :tree-id="treeId"
            :root='rootElement'
            @toggle='toggle(item)'
            @loading='loading($event)'
            @render='renderTreeView'
          )
  fp-loader(v-if='loader')
</template>

<script>
  import virtualList from 'vue-virtual-scroll-list'
  import './FpTree.less'
  import get from 'lodash/get'
  import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
  import FpTreeHeader from './FpTreeHeader'
  import FpTreeItem from './FpTreeItem'
  import FpTreeFlat from './FpTreeFlat'
  import FpTreeBorderCompute from './FpTreeBorderCompute'

  export default {
    components: { FpTreeHeader, FpTreeItem, virtualList },
    props: {
      options: {
        type: Object,
        required: true
      }
    },
    data () {
      let that = this
      return {
        search: '',
        loader: false,
        rows: [],
        sorted: {
          index: 0,
          reverse: false
        },
        rootElement: {
          children: [],
          type: 'root',
          parent: null
        },
        treeId: (new Date().getTime() + Math.floor((Math.random() * 10000) + 1)).toString(16),
        globalActions: {
          add (newItem) {
            that.rootElement.children.push(newItem)
            that.renderTreeView()
          },
          addToParent (newItem) {
            that.rootElement.parent.push(newItem)
            that.renderTreeView()
          },
          update (item) {
            const findQuery = (items) => {
              for (let idx in items) {
                let tmp = items[idx]
                if (item.id === tmp.id) return tmp
                if (tmp.children) {
                  let find = findQuery(tmp.children)
                  if (find) return find
                }
              }
              return null
            }
            let query = findQuery(that.rootElement.children)
            if (query) {
              Object.keys(item).forEach(key => that.$set(query, key, item[key]))
            }
          },
          remove () {
            that.rootElement.children = that.rootElement.children.filter(v => !v.new)
            that.renderTreeView()
          },
        },
        remainSize: 0,
        cellSize: 32
      }
    },
    beforeDestroy () {
      window.removeEventListener('resize',this.onResize)
    },
    mounted () {
      window.addEventListener('resize', this.onResize)
      this.onResize()
      Promise.resolve(this.options.items)
        .then(items => {
          items.forEach(item => this.rootElement.children.push(item))

          if (this.options.id) {
            try {
              this.sorted = JSON.parse(localStorage.getItem('fp-tree-' + this.options.id + '-sort'))
            } catch (_) {
              // eslint-disable-next-line
            }
          }
          this.sorted = this.sorted || this.options.sort || {}
          this.sorted.function = get(this,'options.sort.function')
          this.sorted.index = this.sorted.index || 0
          this.sorted.reverse = this.sorted.reverse || false
           window.addEventListener('resize', () => {
              if (this.$refs.treeRoot) {
                let vScroll = this.$refs.treeRoot.clientHeight
                this.remainSize = vScroll / this.cellSize
              }
            })

           if (this.$refs.treeRoot) {
            let vScroll = this.$refs.treeRoot.clientHeight
            this.remainSize = vScroll / this.cellSize
           }
          this.renderTreeView()
          this.onResize()
        })
    },
    computed: {
      columnRatio() {
        let columnRatio = 0
        this.options.columns.forEach(column => {
          columnRatio += column.grow||1
        })
        return columnRatio
      }
    },
    methods: {
      onResize() {
        if (this.$refs.treeRoot) {
          let vScroll = this.$refs.treeRoot.clientHeight
          this.remainSize = vScroll / this.cellSize
        }
      },
      saveSort () {
        if (this.options.id) {
          localStorage.setItem('fp-tree-' + this.options.id + '-sort', JSON.stringify(this.sorted))
        }
      },
      sort (column, index) {
        if (!column.sortable) return
        if (this.sorted.index === index) {
          this.sorted.reverse = !this.sorted.reverse
        } else {
          this.sorted.index = index
          this.sorted.reverse = false
        }
        this.saveSort()
        this.renderTreeView()
      },
      async toggle (item) {
        if(typeof(item.children) === 'function') {
          item.children = await item.children()
        }
        this.$set(item, 'open', !item.open)
        await this.renderTreeView()
      },
      loading(event) {
        this.loader = event
      },
      async refresh (item) {
        if(typeof(item.children) === 'function') {
          await item.children
        }
        this.$set(item, 'open', !item.open)
        await this.renderTreeView()
      },
      call (action) {
        action.click.call(this.globalActions, this.rootElement)
      },
      searchChange (event) {
        this.search = event.target.value
        this.renderTreeView()
      },
      async renderTreeView () {
        let rows = await FpTreeFlat(this.rootElement, this.search, this.options.type, this.sorted)
        rows = FpTreeBorderCompute(rows)
        rows = rows.map((row,index)=>{
          row.key = row.key||row.id
          row.isLast = index === rows.length - 1
          row.index = index
          return row
        })
        this.rows.splice(0, this.rows.length)
        rows.forEach(row => this.rows.push(row))
        if(this.$refs.virtualList) this.$refs.virtualList.forceRender()
      }
    }
  }
</script>
